import { useEffect, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import {
  LogOut, Mail, Briefcase, Building2, X, Volume2, VolumeX, Send, BellRing,
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../../contexts/AuthContext';
import { getUser } from '../../api/chat';
import Avatar from '../chat/Avatar';
import {
  selectSoundOnSend,
  selectSoundOnReceive,
  setSoundOnSend,
  setSoundOnReceive,
} from '../../redux/slices/chatPreferences';
import { playSendSound, playReceiveSound } from '../../utils/chat/sounds';

interface Props {
  collapsed: boolean;
}

interface Identity {
  designation?: string | null;
  department_name?: string | null;
  role?: string | null;
}

export default function ProfileTab({ collapsed }: Props) {
  const { user, logout } = useAuth();
  const [identity, setIdentity] = useState<Identity>({});
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const soundOnSend = useSelector(selectSoundOnSend);
  const soundOnReceive = useSelector(selectSoundOnReceive);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      const u = await getUser(user.id);
      if (cancelled || !u) return;
      setIdentity({
        designation: u.designation,
        department_name: u.department_name,
        role: u.role,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  if (!user) return null;

  const name = user.name || user.email || 'User';

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="gt-sidebar-item"
          aria-label="Profile"
          title={collapsed ? name : undefined}
          style={{
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: 10,
            width: '100%',
          }}
        >
          <Avatar name={name} size={7} />
          {!collapsed && (
            <span
              className="gt-sidebar-label"
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textAlign: 'left',
                flex: 1,
              }}
            >
              {name}
            </span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="right"
          align="end"
          sideOffset={12}
          style={{
            width: 280,
            background: 'var(--gt-surface, #fff)',
            border: '1px solid var(--gt-divider, rgba(0,0,0,.08))',
            borderRadius: 12,
            boxShadow: '0 12px 32px rgba(0,0,0,.14)',
            overflow: 'hidden',
            zIndex: 60,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 12px',
              borderBottom: '1px solid var(--gt-divider, rgba(0,0,0,.06))',
            }}
          >
            <span style={{ fontWeight: 600, fontSize: 13 }}>Profile</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              style={{
                background: 'transparent',
                border: 0,
                padding: 4,
                cursor: 'pointer',
                color: 'var(--gt-text-muted, #6b7280)',
                display: 'flex',
              }}
            >
              <X size={14} />
            </button>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '16px 12px',
              borderBottom: '1px solid var(--gt-divider, rgba(0,0,0,.06))',
            }}
          >
            <Avatar name={name} size={14} />
            <div
              style={{
                marginTop: 8,
                fontSize: 15,
                fontWeight: 700,
                textAlign: 'center',
                wordBreak: 'break-word',
              }}
            >
              {name}
            </div>
            {(identity.role || user.role) && (
              <span
                style={{
                  marginTop: 6,
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  padding: '2px 8px',
                  borderRadius: 999,
                  color: 'var(--gt-accent, #1f4d3f)',
                  background: 'var(--gt-accent-soft, #e5f3ec)',
                }}
              >
                {identity.role || user.role}
              </span>
            )}
          </div>
          <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {user.email && (
              <ProfileRow icon={<Mail size={14} />} label="Email" value={user.email} />
            )}
            {identity.designation && (
              <ProfileRow icon={<Briefcase size={14} />} label="Designation" value={identity.designation} />
            )}
            {identity.department_name && (
              <ProfileRow icon={<Building2 size={14} />} label="Department" value={identity.department_name} />
            )}
          </div>
          <div
            style={{
              borderTop: '1px solid var(--gt-divider, rgba(0,0,0,.06))',
              padding: '10px 14px 12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div
              style={{
                fontSize: 10,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: 'var(--gt-text-muted, #6b7280)',
                marginBottom: 2,
              }}
            >
              Sounds
            </div>
            <SoundToggle
              icon={<Send size={13} />}
              label="On send"
              enabled={soundOnSend}
              onChange={(v) => {
                dispatch(setSoundOnSend(v));
                if (v) playSendSound();
              }}
            />
            <SoundToggle
              icon={<BellRing size={13} />}
              label="On receive"
              enabled={soundOnReceive}
              onChange={(v) => {
                dispatch(setSoundOnReceive(v));
                if (v) playReceiveSound();
              }}
            />
          </div>
          <div style={{ borderTop: '1px solid var(--gt-divider, rgba(0,0,0,.06))', padding: 8 }}>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                void logout();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                padding: '8px 10px',
                background: 'transparent',
                border: 0,
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 13,
                color: '#dc2626',
                fontWeight: 600,
              }}
            >
              <LogOut size={14} />
              Logout
            </button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function SoundToggle({
  icon,
  label,
  enabled,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  enabled: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      onClick={() => onChange(!enabled)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '6px 8px',
        background: 'transparent',
        border: 0,
        borderRadius: 8,
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <span
        aria-hidden
        style={{
          color: enabled ? 'var(--gt-accent, #1f4d3f)' : 'var(--gt-text-muted, #9ca3af)',
          display: 'flex',
        }}
      >
        {icon}
      </span>
      <span style={{ flex: 1, fontSize: 13, color: 'var(--gt-text-primary, #111827)' }}>
        {label}
      </span>
      <span
        aria-hidden
        style={{
          color: enabled ? 'var(--gt-accent, #1f4d3f)' : 'var(--gt-text-muted, #9ca3af)',
          display: 'flex',
          marginRight: 6,
        }}
      >
        {enabled ? <Volume2 size={13} /> : <VolumeX size={13} />}
      </span>
      <span
        aria-hidden
        style={{
          width: 30,
          height: 17,
          background: enabled ? 'var(--gt-accent, #1f4d3f)' : '#cbd5e1',
          borderRadius: 999,
          position: 'relative',
          transition: 'background-color 180ms ease',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: enabled ? 15 : 2,
            width: 13,
            height: 13,
            background: '#fff',
            borderRadius: '50%',
            transition: 'left 180ms ease',
            boxShadow: '0 1px 2px rgba(0,0,0,.18)',
          }}
        />
      </span>
    </button>
  );
}

function ProfileRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
      <span
        aria-hidden
        style={{ color: 'var(--gt-text-muted, #6b7280)', marginTop: 2, flexShrink: 0 }}
      >
        {icon}
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: 'var(--gt-text-muted, #6b7280)',
          }}
        >
          {label}
        </div>
        <div style={{ fontSize: 13, color: 'var(--gt-text-primary, #111827)', wordBreak: 'break-word' }}>
          {value}
        </div>
      </div>
    </div>
  );
}
